import merge from 'lodash/merge';

import fetch from '../utils/fetch';
import {deleteAuthCookie, saveAuthCookie} from '../utils/cookie';
import {REFRESH_FAILURE, REFRESH_REQUEST, REFRESH_SUCCESS} from "../reducers/routeReducers/auth";
import {tokenSelector} from "../selectors/auth";
import {setLogoutUser} from "../reducers/layout";

function requestRefreshToken() {
  return {
    type: REFRESH_REQUEST,
  };
}

export function receiveRefreshToken(token, user, refToken) {
  return {
    type: REFRESH_SUCCESS,
    token,
    user,
    refToken
  };
}

function refreshTokenError(message) {
  return {
    type: REFRESH_FAILURE,
    message,
  };
}

const handleError = (error, errorCode) => {
  console.error(`API returned ${errorCode} status code:`, error);

  let errorMessage = 'Some error occured';

  if (error.detail) {
    errorMessage = error.detail;
  }

  if (error.email) {
    errorMessage = error.email[0];
  }

  if (typeof error === 'string') {
    errorMessage = error;
  }

  return {errorMessage, errorCode};
};

export const getClientToken = () => {
  if (this.CLIENT_TOKEN) {
    return Promise.resolve({token: this.CLIENT_TOKEN});
  }

  const config = {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      "Cache-Control": "no-store, no-cache"
    },
    body: JSON.stringify({
      client_id: process.env.SAG_CLIENT_ID,
      client_secret: process.env.SAG_CLIENT_SECRET,
    }),
  };

  return fetch(`${process.env.SAG_ROOT}/login-client`, config).then(
    response => {
      if (response.headers.get('content-type') !== 'application/json') {
        return {
          error: response.statusText,
          errorCode: response.status,
        };
      }

      return response.json().then(json => {
        if (!response.ok) {
          return {
            error: json,
            errorCode: response.status,
          };
        }

        const data = json.results;

        this.CLIENT_TOKEN = data.client_token;

        setTimeout(() => {
          this.CLIENT_TOKEN = null;
        }, 24 * 3600 * 1000);
        return {token: data.client_token};
      });
    },
  );
};

export const loginUser = (clientToken, email, password) => {
  const config = {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      'Client-Access-Token': clientToken,
    },
    body: JSON.stringify({
      email,
      password,
    }),
  };
  // return fetch(`${process.env.SAG_ROOT}auth/login/`, config).then(response => {
  return fetch(`${process.env.SAG_ROOT}/login-user-email`, config).then(
    response => {
      if (response.headers.get('content-type') !== 'application/json') {
        return {
          error: response.statusText,
          errorCode: response.status,
        };
      }

      return response.json().then(json => {
        if (!response.ok) {
          return {
            error: json,
            errorCode: response.status,
          };
        }

        const data = json.results;
        return data;
      });
    },
  ).catch(error => {
     console.log(error)
  });
};

export const uidLogin = (clientToken, hash, uid, ref, email) => {
  const config = {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Client-Access-Token': clientToken,
    },
    body: JSON.stringify({
      hash,
      uid,
      referrer_host: ref,
      email,
    }),
  };

  // return fetch(`${process.env.SAG_ROOT}auth/login/`, config).then(response => {
  return fetch(
    `${process.env.SAG_ROOT}/authenticate-custom-navigation`,
    config,
  ).then(response => {
    if (response.headers.get('content-type') !== 'application/json') {
      return {
        error: response.statusText,
        errorCode: response.status,
      };
    }

    return response.json().then(json => {
      if (!response.ok) {
        return {
          error: json,
          errorCode: response.status,
        };
      }

      const data = json.results;

      return data;
    });
  });
};

export const ssoLogin = (clientToken, orgId, authData) => {
  const config = {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Client-Access-Token': clientToken,
    },
    body: JSON.stringify({
      organization_id: orgId,
      ...authData,
    }),
  };

  // return fetch(`${process.env.SAG_ROOT}auth/login/`, config).then(response => {
  return fetch(
    `${process.env.SAG_ROOT}/perform-sso-authentication`,
    config,
  ).then(response => {
    if (response.headers.get('content-type') !== 'application/json') {
      return {
        error: response.statusText,
        errorCode: response.status,
      };
    }

    return response.json().then(json => {
      if (!response.ok) {
        return {
          error: json,
          errorCode: response.status,
        };
      }

      const data = json.results;

      return data;
    });
  });
};

const loginUserSessionApi = (clientToken, sessionId) => {
  const config = {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      'Client-Access-Token': clientToken,
    },
  };

  return fetch(
    `${process.env.SAG_ROOT}/login-session?session_id=${sessionId}`,
    config,
  ).then(response => {
    if (response.headers.get('content-type') !== 'application/json') {
      return {
        error: response.statusText,
        errorCode: response.status,
      };
    }

    return response.json().then(json => {
      if (!response.ok) {
        return {
          error: json,
          errorCode: response.status,
        };
      }

      const data = json.results;

      return data;
    });
  });
};

export const loginUserSession = sessionId =>
  getClientToken()
    .then(({token: clientToken, error, errorCode}) => {
      if (error) {
        return handleError(error, errorCode);
      }

      return loginUserSessionApi(clientToken, sessionId).then(
        ({error: newError, errorCode: newErrorCode, ...data}) => {
          if (newError) {
            return handleError(newError, newErrorCode);
          }

          const {
            refresh_token: refreshToken,
            subdomain_url: subdomain,
            user_token: userToken,
            id: userId,
          } = data;

          if (!userToken) {
            return {};
          }

          const user = merge({}, data, {user_id: userId});
          delete user.user_token;
          delete user.refresh_token;
          delete user.redirection_token;
          delete user.id;
          saveAuthCookie(subdomain);

          return {
            userToken,
            refreshToken,
            user,
            subdomain,
          };
        },
      );
    })
    .catch(err => ({ errorMessage: err.message }));

export const refreshUser = refreshToken => async dispatch => {
  const {token: clientToken} = await getClientToken();
  const config = {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Client-Access-Token': clientToken,
      // 'User-Access-Token': userToken,
    },
    body: JSON.stringify({
      refresh_token: refreshToken,
    }),
  };
  dispatch(requestRefreshToken());
  return fetch(`${process.env.SAG_ROOT}/refresh-token`, config).then(
    response => {
      // return fetch(`${process.env.SAG_ROOT}/auth/token/user/email/`, config).then(response => {
      if (response.headers.get('content-type') !== 'application/json') {
        return {
          error: response.statusText,
          errorCode: response.status,
        };
      }

      return response.json().then(json => {
        if (!response.ok) {
          return {
            error: json,
            errorCode: response.status,
          };
        }

        const data = json.results;
        const {
          refresh_token: refToken,
          user_token: token,
          id: userId,
        } = data;

        if (!token) {
          return Promise.resolve(token);
        }

        const userData = merge({}, data, {user_id: userId});
        delete userData.user_token;
        delete userData.refresh_token;
        delete userData.id;
        dispatch(receiveRefreshToken(token, userData, refToken));
        // dispatch(receiveRefreshToken(token, userData, refToken, true));

        return Promise.resolve(refToken);
      });
    },
  ).catch(err => {
    dispatch(refreshTokenError(err));
  });
};

export const refreshAuth = async auth => {
  console.info('refreshing user', auth.refreshToken);

  const {error, errorCode, ...data} = await refreshUser(
    auth.refreshToken
  );

  if (error) {
    if (__DEV__) {
      console.error(error, errorCode); // eslint-disable-line
    }
    return {};
  }

  const {
    refresh_token: refreshToken,
    user_token: userToken,
    id: userId,
  } = data;

  const user = merge({}, data, { user_id: userId });
  delete user.user_token;
  delete user.refresh_token;
  delete user.redirection_token;
  delete user.id;

  console.info('refreshing user successful');

  return merge({}, auth, { userToken, user, refreshToken });
};

export const forgotPassword = (clientToken, email) => {
  const config = {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Client-Access-Token': clientToken,
    },
    body: JSON.stringify({
      email,
      action: 'forgot_password',
    }),
  };

  return fetch(`${process.env.SAG_ROOT}/send-user-email`, config).then(
    response => {
      // return fetch(`${process.env.SAG_ROOT}/auth/token/user/email/`, config).then(response => {
      if (response.headers.get('content-type') !== 'application/json') {
        return {
          error: response.statusText,
          errorCode: response.status,
        };
      }

      return response.json().then(json => {
        if (!response.ok) {
          return {
            error: json,
            errorCode: response.status,
          };
        }

        const data = json.results;

        return data;
      });
    },
  );
};

export const updatePassword = (clientToken, userToken, newPass, newPass2) => {
  const config = {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Client-Access-Token': clientToken,
      'User-Access-Token': userToken,
    },
    body: JSON.stringify({
      new_password: newPass,
      confirm_password: newPass2,
    }),
  };

  return fetch(`${process.env.SAG_ROOT}/update-user-details`, config).then(
    response => {
      // return fetch(`${process.env.SAG_ROOT}/auth/token/user/email/`, config).then(response => {
      if (response.headers.get('content-type') !== 'application/json') {
        return {
          error: response.statusText,
          errorCode: response.status,
        };
      }

      return response.json().then(json => {
        if (!response.ok) {
          return {
            error: json,
            errorCode: response.status,
          };
        }

        const data = json.results;

        return data;
      });
    },
  );
};

export const getOrgDetailsBySubdomain = (clientToken, subdomain) => {
  const config = {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Client-Access-Token': clientToken,
    },
    body: JSON.stringify({subdomain}),
  };

  return fetch(
    `${process.env.SAG_ROOT}/get-org-details-by-subdomain`,
    config,
  ).then(response => response.json().then(json => {
    if (!response.ok) {
      return {
        error: json,
        errorCode: response.status,
      };
    }

    const data = json.results;

    return data;
  }));
};

export const getOrgAuthMech = (clientToken, orgId) => {
  const config = {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      'Client-Access-Token': clientToken,
    },
  };

  return fetch(
    `${process.env.SAG_ROOT}/get-org-auth-mechanism?organization_id=${orgId}`,
    config,
  ).then(response => response.json().then(json => {
    if (!response.ok) {
      return {
        error: json,
        errorCode: response.status,
      };
    }

    const data = json.results;

    return data;
  }));
};


const refreshUserTokenClient = () => (dispatch, getState) => {
  const thisVar = {};

  const {isFetching} = getState().auth;

  if (isFetching && thisVar && thisVar.refreshTokenPromise) {
    return thisVar.refreshTokenPromise;
  }

  // const refreshToken = getCookieValue('refresh_token');
  const {refreshToken} = getState().auth;
  /* New Auth Flow getting refresh token from redux instead of Browser cookie */

  thisVar.refreshTokenPromise = dispatch(
    refreshUser(refreshToken),
  ).catch(error => {
    if (process.env.BROWSER && error.message === 'Failed to fetch') {
      alert(
        'Either no Internet Connectivity or the Servers are not responding',
      );
    }
    return Promise.reject(error);
  });

  return thisVar.refreshTokenPromise;
};

const refreshUserTokenServer = (refreshToken) => async dispatch => {
  let token = null;
  try {
    token = await dispatch(refreshUser(refreshToken));
    // await onAuthSuccess(dispatch);
  } catch (err) {
    console.error(err);
  }

  return token;
};

export const refreshUserToken = process.env.BROWSER
  ? refreshUserTokenClient
  : refreshUserTokenServer;


export const logoutUser = () => async (dispatch, getState) => {
  // dispatch(requestLogout());
  getClientToken().then(clientToken => {
    console.log({clientToken})
    const userToken = tokenSelector(getState().routes);
    // const decodeUserToken = decodeURIComponent(`${userToken}`);
    const {token} = clientToken;
    const config = {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        'Client-Access-Token': token,
        'User-Access-Token': userToken,
      },
      body: JSON.stringify({}),
    };
    return fetch(`${process.env.SAG_ROOT}/logout`, config)
      .then(response =>
        response.json().then(json => {
          try {
            window.sessionStorage.clear();
            window.localStorage.clear();
            deleteAuthCookie();
            dispatch(setLogoutUser());
            window.localStorage.setItem('logout', Date.now());
            window.location.reload();
          } catch (err) {
            console.log(err);
          }
        }),
      )
      .catch(err => {
        if (typeof window === 'object') {
          window.location.reload();
        }
        Promise.reject(err);
      });
  });
};



// WEBPACK FOOTER //
// ./src/actions/sagAuth.js